import { IDP_PROVIDER_URL } from "../Settings";

export interface DrxProfile {
    profile_id: string;
    display_name?: string;
    roles?: string[];
}

/**
 * Fetch the user's profiles straight from the IDP.
 *
 * The IDP resolves these live from the Hub on every call, so this is the only
 * authoritative answer to "which profiles does this user have right now".
 *
 * drx-frontend-api caches the list in localStorage ("drx-resolver-options") at
 * login and only refreshes it as a side effect of switchProfile — on a page
 * refresh it rehydrates that cache and never re-checks. So its `profiles` can
 * still name a profile that has since been removed in the Hub, and validating a
 * stored selection against it compares one stale value to another. Hence this
 * direct call.
 *
 * Returns null when there is nothing to reconcile (no session, visitor, or the
 * IDP is unreachable) so callers can leave existing state untouched.
 */
export const fetchProfilesFromIdp = async (): Promise<DrxProfile[] | null> => {
    const tokenJson = localStorage.getItem('drx-identity-token')
    const activeProvider = localStorage.getItem('activeProvider')
    if (!tokenJson || !activeProvider) return null

    let originalToken: string | undefined
    try {
        originalToken = JSON.parse(tokenJson)?.original_token
    } catch {
        return null
    }
    // Visitors (share links) have no original token and no profiles.
    if (!originalToken) return null

    try {
        const providers = await (await fetch(`${IDP_PROVIDER_URL}/providers`)).json()
        // drx_client_id is not derivable from the provider id (e.g. provider
        // "vault" uses drx_client_id "imro"), so it has to be looked up.
        const drxClientId = (providers as any[])?.find(
            (p) => p?.id === activeProvider
        )?.drx_client_id
        if (!drxClientId) return null

        const response = await fetch(`${IDP_PROVIDER_URL}/resolver_options`, {
            method: 'POST',
            headers: {
                'X-CPD-AUTHORIZE': originalToken,
                'X-CPD-CLIENT-ID': drxClientId,
            },
        })
        if (!response.ok) return null

        const data = await response.json()
        return Array.isArray(data?.profiles) ? data.profiles : null
    } catch (error) {
        console.error('Could not refresh profiles from the IDP:', error)
        return null
    }
}
